<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { StepTimingMap, StepTreeStepNode } from '@/components/step/StepTree.vue'
import Period from '@/components/Period.vue'
import IconIssue from '@/components/icons/IconIssue.vue'
import StepIconList from '@/components/icon/StepIconList.vue'
import { uniqueArray } from '@/app'
import useStepTreeDrag, { ClosingNode, UnderDropChoice } from '@/composables/useStepTreeDrag'

const { t } = useI18n()

const props = defineProps<{
  moduleId: string
  node: StepTreeStepNode
  stepTimings: StepTimingMap
  moduleTotalDuration: number
  depth: number
  dragDropDisabled: boolean
  isLatestBopVersion: boolean
  selectedNodeIds: Set<string>
  emptySectionNodeIds: Set<string>
  // Ancestors whose subtree visually ends at this row; their depths are offered as under-drop targets.
  closingNodes: ClosingNode[]
  nextSiblingNodeId?: string
  isFirstRoot?: boolean
}>()
const emit = defineEmits<{
  moveBefore: [movedNodeId: string, targetNodeId: string]
  moveAfter: [movedNodeId: string, targetNodeId: string]
  makeFirstChild: [movedNodeId: string, targetNodeId: string]
  toggleSelect: [nodeId: string]
  selectNodeId: [value: string]
}>()

const stepNode = computed(() => props.node.stepNode)
const children = computed(() => props.node.children)
const isParent = computed(
  () => children.value.length > 0 || props.emptySectionNodeIds.has(props.node.stepNode.id),
)
const showEmptySectionHint = computed(() => isParent.value && children.value.length == 0)
const isChecked = computed(() => props.selectedNodeIds.has(props.node.stepNode.id))
const isLibraryStep = computed(() => !!stepNode.value.step.libraryStep)
const libraryItemVersion = computed(() => stepNode.value.step.libraryStep?.libraryItemVersion)
const updateAvailable = computed(
  () => props.isLatestBopVersion && !!libraryItemVersion.value?.updateAvailable,
)

function onRowClick(e: MouseEvent) {
  if (e.ctrlKey || e.metaKey) {
    emit('toggleSelect', stepNode.value.id)
    return
  }

  emit('selectNodeId', stepNode.value.id)
}
const timing = computed(() => props.stepTimings[stepNode.value.id] ?? { start: 0, span: 0 })
const offsetPercent = computed(() =>
  props.moduleTotalDuration > 0 ? (timing.value.start / props.moduleTotalDuration) * 100 : 0,
)
const widthPercent = computed(() =>
  props.moduleTotalDuration > 0 ? (timing.value.span / props.moduleTotalDuration) * 100 : 0,
)
const unresolvedIssueCount = computed(() => {
  const all = (props.node.stepNode.step.issueLinks || [])
    .filter((l) => !l.issue.resolvedAt)
    .map((l) => l.issue)
  return uniqueArray(all, (item) => item.id).length
})

const eventFieldNodeId = 'nodeId'

const { draggedNodeId, hoverNodeId, hoverZone, underChoice, clearHover, clearDrag } =
  useStepTreeDrag()
const isDragging = ref(false)

const descendantCount = computed(() => countDescendants(props.node))
function countDescendants(node: StepTreeStepNode): number {
  return node.children.reduce((sum, c) => sum + 1 + countDescendants(c), 0)
}
const childClosingNodes = computed<ClosingNode[]>(() => [
  { nodeId: stepNode.value.id, depth: props.depth },
  ...props.closingNodes,
])

function isHoverZone(zone: string) {
  return hoverNodeId.value == stepNode.value.id && hoverZone.value == zone
}

// Row indent per depth level: 1.5em padding plus 0.3em from each nested sheet.
function lineLeftEm(depth: number) {
  return depth * 1.5 + (depth - props.depth) * 0.3
}

function isParentOfDraggedNode(e: DragEvent) {
  const types = e.dataTransfer?.types || []
  return props.node.ancestryNodeIds.some((id) => types.includes(`ancestry-node-id/${id}`))
}
function dragStart(e: DragEvent) {
  const dt = e.dataTransfer as DataTransfer
  dt.effectAllowed = 'move'
  dt.dropEffect = 'move'
  dt.setData(eventFieldNodeId, stepNode.value.id)
  dt.setData(`ancestry-node-id/${props.node.stepNode.id}`, 'true')
  dt.setDragImage(createDragGhost(), 16, 16)
  draggedNodeId.value = stepNode.value.id
  isDragging.value = true
}
function createDragGhost(): HTMLElement {
  const ghost = document.createElement('div')
  ghost.style.cssText =
    'position:fixed;top:-100px;left:-100px;display:flex;align-items:center;gap:6px;' +
    'padding:4px 12px;border-radius:16px;background:#424242;color:#fff;' +
    'font-size:0.875rem;max-width:20em;white-space:nowrap;overflow:hidden'
  ghost.textContent = stepNode.value.step.name
  if (descendantCount.value > 0) {
    const badge = document.createElement('span')
    badge.style.cssText = 'padding:0 8px;border-radius:10px;background:#00acc1;font-size:0.75rem'
    badge.textContent = `+${descendantCount.value}`
    ghost.appendChild(badge)
  }
  document.body.appendChild(ghost)
  setTimeout(() => ghost.remove())
  return ghost
}
function dragEnd() {
  isDragging.value = false
  clearDrag()
}

function underCandidates(): UnderDropChoice[] {
  if (children.value.length > 0) {
    return [{ targetNodeId: stepNode.value.id, depth: props.depth + 1, firstChild: true }]
  }

  return [
    { targetNodeId: stepNode.value.id, depth: props.depth, firstChild: false },
    ...props.closingNodes.map((c) => ({
      targetNodeId: c.nodeId,
      depth: c.depth,
      firstChild: false,
    })),
  ]
}
// The horizontal pointer position picks the drop depth when a row closes one or more subtrees.
function pickUnderChoice(e: DragEvent): UnderDropChoice {
  const candidates = underCandidates()
  if (candidates.length == 1) {
    return candidates[0]
  }

  const zone = e.currentTarget as HTMLElement
  const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  const relEm = (e.clientX - zone.getBoundingClientRect().left) / remPx
  const sorted = [...candidates].sort((a, b) => b.depth - a.depth)
  return sorted.find((c) => relEm >= lineLeftEm(c.depth)) ?? sorted[sorted.length - 1]
}
function isNoOpUnder(choice: UnderDropChoice) {
  if (choice.firstChild) {
    return children.value[0]?.stepNode.id == draggedNodeId.value
  }
  if (choice.targetNodeId == stepNode.value.id) {
    return props.nextSiblingNodeId == draggedNodeId.value
  }
  return false
}

// No preventDefault for invalid targets, so the browser shows the not-allowed cursor.
function dragOverOn(e: DragEvent) {
  if (isParentOfDraggedNode(e)) {
    return
  }

  e.preventDefault()
  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = 'move'
  }
  hoverNodeId.value = stepNode.value.id
  hoverZone.value = 'on'
  underChoice.value = undefined
}
function dragOverUnder(e: DragEvent) {
  if (isParentOfDraggedNode(e)) {
    return
  }

  const choice = pickUnderChoice(e)
  if (isNoOpUnder(choice)) {
    if (isHoverZone('under')) {
      clearHover()
    }
    return
  }

  e.preventDefault()
  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = 'move'
  }
  hoverNodeId.value = stepNode.value.id
  hoverZone.value = 'under'
  underChoice.value = choice
}
function dragOverTop(e: DragEvent) {
  if (isParentOfDraggedNode(e)) {
    return
  }

  e.preventDefault()
  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = 'move'
  }
  hoverNodeId.value = stepNode.value.id
  hoverZone.value = 'top'
  underChoice.value = undefined
}
function dragLeaveZone(zone: string) {
  if (isHoverZone(zone)) {
    clearHover()
  }
}

function getNodeId(e: DragEvent) {
  return e.dataTransfer?.getData(eventFieldNodeId) || ''
}
function dropOn(e: DragEvent) {
  clearHover()
  const movedNodeId = getNodeId(e)
  if (!movedNodeId || isParentOfDraggedNode(e)) {
    return
  }

  if (children.value.length > 0) {
    emit('moveAfter', movedNodeId, children.value[children.value.length - 1].stepNode.id)
  } else {
    emit('makeFirstChild', movedNodeId, stepNode.value.id)
  }
}
function dropUnder(e: DragEvent) {
  const choice =
    hoverNodeId.value == stepNode.value.id && underChoice.value
      ? underChoice.value
      : pickUnderChoice(e)
  clearHover()
  const movedNodeId = getNodeId(e)
  if (!movedNodeId || isParentOfDraggedNode(e) || isNoOpUnder(choice)) {
    return
  }

  if (choice.firstChild) {
    emit('makeFirstChild', movedNodeId, stepNode.value.id)
  } else {
    emit('moveAfter', movedNodeId, choice.targetNodeId)
  }
}
function dropTop(e: DragEvent) {
  clearHover()
  const movedNodeId = getNodeId(e)
  if (!movedNodeId || isParentOfDraggedNode(e)) {
    return
  }

  emit('moveBefore', movedNodeId, stepNode.value.id)
}
</script>

<template>
  <v-sheet
    class="step-container"
    :class="{
      'step-container--library': isLibraryStep,
      'step-container--drag-source': isDragging,
    }"
    @click.stop="onRowClick"
  >
    <v-row dense class="drop-row" :class="{ 'drop-row--nest-target': isHoverZone('on') }">
      <div
        v-if="props.isFirstRoot"
        class="drop-zone drop-zone--top"
        @dragenter="dragOverTop"
        @dragover="dragOverTop"
        @dragleave="dragLeaveZone('top')"
        @drop.prevent="dropTop"
      />
      <div
        class="drop-zone drop-zone--on"
        @dragenter="dragOverOn"
        @dragover="dragOverOn"
        @dragleave="dragLeaveZone('on')"
        @drop.prevent="dropOn"
      />
      <div
        class="drop-zone drop-zone--under"
        @dragenter="dragOverUnder"
        @dragover="dragOverUnder"
        @dragleave="dragLeaveZone('under')"
        @drop.prevent="dropUnder"
      />
      <div
        v-if="isHoverZone('top')"
        class="drop-insert-line insert-line--top"
        :style="{ left: lineLeftEm(0) + 'em' }"
      />
      <div
        v-if="isHoverZone('under')"
        class="drop-insert-line insert-line--under"
        :style="{ left: lineLeftEm(underChoice?.depth ?? props.depth) + 'em' }"
      />
      <v-col cols="6">
        <div class="d-flex align-center row-content" :style="{ paddingLeft: depth * 1.5 + 'em' }">
          <div
            class="step-draggable"
            :draggable="!props.dragDropDisabled"
            @dragstart="dragStart"
            @dragend="dragEnd"
          >
            <v-chip
              class="step-chip"
              :variant="props.node.isSelected || isChecked ? 'outlined' : 'text'"
              density="compact"
            >
              <v-icon v-if="!props.dragDropDisabled" icon="drag_indicator" class="drag-handle" />
              <v-icon v-if="isParent" icon="folder_open" size="small" class="mr-1" />

              {{ props.node.numbering }}

              <v-chip v-if="unresolvedIssueCount > 0" density="compact" class="ml-1">
                <icon-issue color="error" />
                {{ unresolvedIssueCount }}
              </v-chip>

              <span class="step-label ml-1">
                <span
                  :class="{ 'step-name--parent': isParent }"
                  :title="stepNode.step.icons.length > 0 ? undefined : stepNode.step.name"
                  >{{ stepNode.step.name }}</span
                >
                <step-icon-list
                  :icons="stepNode.step.icons"
                  :size="18"
                  :tooltip-title="stepNode.step.name"
                  compact
                />
              </span>

              <v-chip v-if="children.length > 0" density="compact" size="x-small" class="ml-1">
                {{ children.length }}
              </v-chip>

              <template
                v-if="isLibraryStep || updateAvailable || stepNode.step.detachedFromLibrary"
                #append
              >
                <v-icon
                  v-if="isLibraryStep && !updateAvailable"
                  icon="library_add_check"
                  :size="14"
                  class="ml-1"
                  role="img"
                  :title="t('component.stepTree.libraryStep')"
                  :aria-label="t('component.stepTree.libraryStep')"
                />
                <v-tooltip
                  v-if="updateAvailable"
                  :text="t('component.libraryStepStatus.updateAvailableMessage')"
                  location="top"
                >
                  <template #activator="{ props: tooltipProps }">
                    <v-icon
                      v-bind="tooltipProps"
                      icon="arrow_circle_up"
                      color="info"
                      :size="18"
                      class="ml-1 library-status-icon"
                      role="img"
                      :aria-label="t('component.libraryStepStatus.updateAvailable')"
                    />
                  </template>
                </v-tooltip>
                <v-tooltip
                  v-if="stepNode.step.detachedFromLibrary"
                  :text="t('component.libraryStepStatus.detached')"
                  location="top"
                >
                  <template #activator="{ props: tooltipProps }">
                    <v-icon
                      v-bind="tooltipProps"
                      icon="link_off"
                      :size="14"
                      class="ml-1 library-status-icon"
                      role="img"
                      :aria-label="t('component.libraryStepStatus.detached')"
                    />
                  </template>
                </v-tooltip>
              </template>
            </v-chip>
          </div>
          <span v-if="showEmptySectionHint" class="empty-section-hint ml-2">
            {{ t('component.stepTree.emptySectionHint') }}
          </span>
        </div>
      </v-col>
      <v-col cols="3">
        <v-chip density="compact">{{ stepNode.step.stepType.name }}</v-chip>
      </v-col>
      <v-col cols="3">
        <div class="gantt-track" :class="{ 'gantt-track--parent': isParent }">
          <div
            class="gantt-bar"
            :class="{ 'gantt-bar--parent': isParent }"
            :style="{ left: offsetPercent + '%', width: widthPercent + '%' }"
          />
          <div class="gantt-label">
            <period :seconds="timing.span" />
          </div>
        </div>
      </v-col>
    </v-row>

    <div v-if="children.length > 0" class="children-container">
      <step-tree-node
        v-for="(c, i) in children"
        :key="c.stepNode.id"
        :node="c"
        :depth="depth + 1"
        :step-timings="props.stepTimings"
        :module-total-duration="props.moduleTotalDuration"
        :module-id="props.moduleId"
        :drag-drop-disabled="props.dragDropDisabled"
        :is-latest-bop-version="props.isLatestBopVersion"
        :selected-node-ids="props.selectedNodeIds"
        :empty-section-node-ids="props.emptySectionNodeIds"
        :closing-nodes="i == children.length - 1 ? childClosingNodes : []"
        :next-sibling-node-id="children[i + 1]?.stepNode.id"
        @move-before="(movedNodeId, targetNodeId) => $emit('moveBefore', movedNodeId, targetNodeId)"
        @move-after="(movedNodeId, targetNodeId) => $emit('moveAfter', movedNodeId, targetNodeId)"
        @make-first-child="
          (movedNodeId, targetNodeId) => $emit('makeFirstChild', movedNodeId, targetNodeId)
        "
        @toggle-select="(v) => $emit('toggleSelect', v)"
        @select-node-id="(v) => $emit('selectNodeId', v)"
      />
    </div>
  </v-sheet>
</template>

<style scoped lang="scss">
.step-container {
  position: relative;
  padding-left: 0.3em;
  cursor: pointer;
}
.step-container--drag-source {
  opacity: 0.4;
}
.drag-handle {
  z-index: 110;
  cursor: grab;
}
// Above the drop zones, so their tooltips open on hover.
.library-status-icon {
  z-index: 110;
}
.drop-row {
  position: relative;
}
.drop-row--nest-target {
  z-index: 100;
  background-color: rgba(var(--v-theme-primary), 0.08);
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
  border-radius: 4px;
}
.row-content {
  min-width: 0;
}
.step-draggable {
  min-width: 0;
  max-width: 100%;
}
.step-chip {
  max-width: 100%;

  :deep(.v-chip__content) {
    min-width: 0;
  }
}
.step-chip.v-chip--variant-text {
  border: thin solid transparent;
}
.step-label {
  min-width: 0;
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.step-name--parent {
  font-weight: 500;
}
.empty-section-hint {
  font-style: italic;
  font-size: 0.8rem;
  opacity: 0.6;
  white-space: nowrap;
}
.children-container {
  margin-top: 0.2em;
  padding-bottom: 0.5em;
}
.children-container > .step-container:last-child > .children-container {
  padding-bottom: 0;
}
.gantt-track {
  position: relative;
  width: 100%;
  height: 18px;
  border-radius: 2px;
  overflow: hidden;
  background-color: rgba(var(--v-theme-secondary), 0.12);
}
.gantt-track--parent {
  background-color: rgba(0, 172, 193, 0.12);
}
.gantt-bar {
  position: absolute;
  top: 0;
  height: 100%;
  min-width: 2px;
  background-color: rgb(var(--v-theme-secondary));
}
.gantt-bar--parent {
  background-color: #00acc1;
}
.gantt-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  font-size: 0.75rem;
  color: rgb(var(--v-theme-on-surface));
  pointer-events: none;
}
.drop-zone {
  z-index: 99;
  position: absolute;
  left: 0;
  width: 100%;
}
.drop-zone--top {
  z-index: 100;
  top: 0;
  height: 30%;
}
.drop-zone--on {
  top: 0;
  height: 55%;
}
.drop-zone--under {
  bottom: 0;
  height: 45%;
}
.insert-line--top {
  top: -2px;
}
.insert-line--under {
  bottom: -2px;
}
</style>
